import { cn } from '@/lib/cn';

interface AgeStepperProps {
  value: number | null;
  onChange: (age: number) => void;
  min?: number;
  max?: number;
}

/**
 * Селектор возраста для экрана 2 (ChildInfo).
 * Структура из дизайна iter1_base-03:
 *  - Большой степпер «− 9 лет +» в верхней части.
 *  - Ряд быстрых кнопок 6,7,8,9,10,11,12,13,14.
 *  - Подпись «Группа: младшая/средняя/старшая» под кнопками.
 */
const AGE_OPTIONS = [6, 7, 8, 9, 10, 11, 12, 13, 14];
const AGE_GROUP_LABELS: Record<number, string> = {
  6: 'младшая',
  7: 'младшая',
  8: 'младшая',
  9: 'средняя',
  10: 'средняя',
  11: 'средняя',
  12: 'старшая',
  13: 'старшая',
  14: 'старшая',
};

export function AgeStepper({ value, onChange, min = 6, max = 14 }: AgeStepperProps) {
  const display = value ?? 9;
  const decrement = () => {
    if (display > min) onChange(display - 1);
  };
  const increment = () => {
    if (display < max) onChange(display + 1);
  };

  const groupLabel = value !== null ? AGE_GROUP_LABELS[value] : null;

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          aria-label="Уменьшить возраст"
          onClick={decrement}
          disabled={display <= min}
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-md',
            'bg-background border border-border text-text-primary text-2xl no-tap-highlight',
            'active:bg-border disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          −
        </button>
        <div className="flex flex-1 items-baseline justify-center gap-2">
          <span className="text-4xl font-bold leading-none text-text-primary">
            {value ?? '—'}
          </span>
          <span className="text-sm text-text-secondary">лет</span>
        </div>
        <button
          type="button"
          aria-label="Увеличить возраст"
          onClick={increment}
          disabled={display >= max}
          className={cn(
            'flex h-11 w-11 items-center justify-center rounded-md',
            'bg-background border border-border text-text-primary text-2xl no-tap-highlight',
            'active:bg-border disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          +
        </button>
      </div>

      <div className="mt-4 grid grid-cols-9 gap-1">
        {AGE_OPTIONS.map((age) => {
          const selected = value === age;
          return (
            <button
              key={age}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(age)}
              className={cn(
                'h-10 rounded-md text-sm font-medium no-tap-highlight transition-colors',
                'border',
                selected
                  ? 'bg-accent border-accent text-white'
                  : 'bg-background border-border text-text-primary active:bg-border',
              )}
            >
              {age}
            </button>
          );
        })}
      </div>

      {groupLabel && (
        <p className="mt-3 text-center text-xs text-text-secondary">Группа: {groupLabel}</p>
      )}
    </div>
  );
}
